"use client";
import { Plus } from "@cmp-src/icons/TacticIcons/Plus";
import { SidebarSearchAnimated } from "@cmp-src/icons/TacticIcons/SidebarSearchAnimated";
import { OrganizationSwitcher } from "@cmp-src/organizations/OrganizationSwitcher/OrganizationSwitcher";
import { OrgLogo } from "@cmp-src/organizations/OrgLogo/OrgLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@cmp-src/primitives/dropdown-menu";
import { Link } from "@cmp-src/primitives/Link";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@cmp-src/primitives/tooltip";
import { useAskTessa } from "@cmp-src/tessa/AskTessaProvider";
import {
  AuthenticatedUser,
  OrganizationSettings,
  Permissions,
  UserRoles
} from "@gettactic/api";
import { MARIN_ROUTES } from "@gettactic/defines/src/web-routes";
import { cn, useDisclosure } from "@gettactic/helpers";
import { isSchedulesDisabled } from "@gettactic/helpers/src/featureFlags";
import { authenticatedUserIsInMoreOrganizations } from "@gettactic/helpers/src/features/authenticatedUserIsInMoreOrganizations";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { isTacticAppWebView } from "@gettactic/helpers/src/isTacticAppWebView";
import { getControlKey } from "@gettactic/helpers/src/os-helpers";
import { useAuthenticated, useOrgFeatureFlag } from "@gettactic/hooks";
import { MenuIcon } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { MobileGlobalReserveActionSheet } from "@/features/dashboard/components/mobile/MobileGlobalReserveActionSheet";
import { useRouterPush } from "@/features/dashboard/helpers/router/useRouterPush";
import { useGlobalSearch } from "@/features/global-search";
import { useTeamsApp } from "@/features/microsoft-teams/hooks/useTeamsApp";
import { CancelReservationsModal } from "@/features/reserve/components/CancelReservations/CancelReservationsModal";
import { useReserve } from "@/features/reserve/providers/context";
import { ChatBotBubble } from "@/features/tessa/components/bubble/ChatBotBubble";
import { useFeatureAnnouncement } from "@/providers/FeatureAnnouncementProvider";
import { useLayout } from "@/providers/LayoutProvider/context";
import { LayoutMenuItem } from "@/providers/LayoutProvider/types";
import { LoggedUserNav } from "@/ui/layout/controls/LoggedUserNav/LoggedUserNav";
import { ImpersonationButton } from "./controls/ImpersonationButton";

export const roundButtonClassNames =
  "flex h-[36px] w-[36px] items-center justify-center rounded-full";
const classes = {
  Wrapper:
    "justify-between w-full fixed left-0 right-0 z-sticky md:static md:h-full py-2 md:py-5 md:overflow-hidden flex md:flex-col",
  Logo: "w-6 md:w-auto hidden md:block text-primary-bg relative",
  SectionsContainer:
    "md:border-t-0 h-13 bg-red-200 md:h-auto items-end md:items-start md:shadow-none fixed py-2 md:py-0 md:bg-transparent flex justify-center md:justify-start md:flex-col md:static z-sticky bottom-0 left-0 right-0 md:mt-6 gap-3 md:flex-1 overflow-hidden",
  LogoIcon: "size-9 cursor-pointer",
  MenuItem:
    "size-9 overflow-hidden p-1.5 transition-colors hover:text-v2-sidebar-icons-selected hover:cursor-pointer",
  MenuItemActive: "text-v2-sidebar-icons-selected",
  MenuItemNotActive: "text-v2-sidebar-icons",
  Icon: "size-6",
  FooterContainer:
    "hidden md:flex flex-row md:flex-col gap-5 md:mt-5 items-center justify-center",
  PlusButton: `${roundButtonClassNames} hidden md:flex bg-primary text-primary-text`,
  MdPlusButton: `${roundButtonClassNames} md:hidden bg-primary text-primary-text`,
  PlusIcon: "size-[18px]",
  ExtraButton: "p-1.5",
  ExtraIcon: "size-6"
};

const isSectionEnabled = (
  enabled: boolean | LayoutMenuItem["enabled"],
  permissions: Permissions,
  featureFlags: NonNullable<OrganizationSettings["feature_flags"]>,
  userRoles: UserRoles[],
  authenticatedUser: AuthenticatedUser
): boolean => {
  if (typeof enabled === "function") {
    return enabled(permissions, featureFlags, userRoles, authenticatedUser);
  }
  return enabled;
};

export const MainSidebar: React.FC = () => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const askTessa = useAskTessa();
  const workplaceRequestsFeatureFlag = useOrgFeatureFlag("workplace_requests");
  const tessaFeatureFlag = useOrgFeatureFlag("tessa");
  const visitorManagementFeatureFlag = useOrgFeatureFlag("visitor_management");
  const roomDisplaysFeatureFlag = useOrgFeatureFlag("room_displays");
  const timelineFeatureFlag = useOrgFeatureFlag("timeline");
  const mapFeatureFlag = useOrgFeatureFlag("map");
  const dashboardFeatureFlag = useOrgFeatureFlag("dashboard");
  const reportingFeatureFlag = useOrgFeatureFlag("reporting");
  const organizationSwitcherDisclosure = useDisclosure();
  const { isBreakpoint } = useBreakpoint();
  const { sidebarSize, navSections, selectedFloor } = useLayout();
  const isMobileApp = isTacticAppWebView();
  const isTeamsApp = !!useTeamsApp();

  const { openReserveModal } = useReserve();
  const routerPush = useRouterPush();
  const { t } = useTranslation("common");
  const mobileReserveSheetDisclosure = useDisclosure();
  const cancelReservationsDisclosure = useDisclosure();
  const globalSearch = useGlobalSearch();
  const { openAllFeaturesModal } = useFeatureAnnouncement();

  const isMd = isBreakpoint("md");

  const handleReserveRoom = (e: React.MouseEvent) => {
    // we gotta wait for the dropdown to close before opening the modal
    // otherwise the modal will open and close immediately

    openReserveModal({
      resourceType: "meeting_room"
    });
  };

  const handleReserveDesk = (e: React.MouseEvent) => {
    // we gotta wait for the dropdown to close before opening the modal
    // otherwise the modal will open and close immediately

    openReserveModal({
      resourceType: "desk"
    });
  };

  const inMoreOrganizations =
    authenticatedUserIsInMoreOrganizations(authenticatedUser);

  const featureFlags: NonNullable<OrganizationSettings["feature_flags"]> = {
    workplace_requests: workplaceRequestsFeatureFlag ?? false,
    tessa: tessaFeatureFlag ?? false,
    visitor_management: visitorManagementFeatureFlag ?? false,
    room_displays: true,
    wayfinder_displays: true,
    timeline: timelineFeatureFlag ?? false,
    map: mapFeatureFlag ?? false,
    dashboard: dashboardFeatureFlag ?? false,
    reporting: reportingFeatureFlag ?? false
  };

  const currentOrgSlug = authenticatedUser?.organization?.slug || "";
  const userRoles = authenticatedUser.user?.organization_user?.roles ?? [];
  const visibleNavSections = React.useMemo(() => {
    const sections = navSections.filter(
      (section) =>
        (isMd || !section.extraOnMobile) &&
        isSectionEnabled(
          section.enabled,
          authenticatedUser.permissions,
          featureFlags,
          userRoles,
          authenticatedUser
        ) &&
        !(
          section.id === "timeline" && isSchedulesDisabled(authenticatedUser)
        ) &&
        // Per-tenant gating: when a nav item lists `orgSlugs`, only show
        // it when the current org's slug is in that list.
        (!section.orgSlugs || section.orgSlugs.includes(currentOrgSlug))
    );
    if (!isMd) {
      sections.push({
        id: "more",
        title: "More",
        enabled: true,
        icon: () => <MenuIcon className={"size-6"} />,
        url: "#",
        active: false,
        appendFloorId: false,
        extraOnMobile: false,
        isMoreButton: true
      });
    }
    return sections;
  }, [
    navSections,
    isMd,
    authenticatedUser,
    userRoles,
    currentOrgSlug,
    workplaceRequestsFeatureFlag,
    tessaFeatureFlag,
    visitorManagementFeatureFlag,
    roomDisplaysFeatureFlag
  ]);
  const isDesktop = isBreakpoint("md");
  const getGlobalSearchButton = (type: "desktop" | "mobile" = "desktop") => {
    if (!isDesktop) {
      return null;
    }
    const button = (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className={cn(
              type === "mobile" ? classes.MdPlusButton : classes.PlusButton,
              "ml-[3px] bg-transparent text-v2-sidebar-icons transition-2 transition-all"
            )}
            onClick={() => globalSearch.open("")}
          >
            <SidebarSearchAnimated />
          </button>
        </TooltipTrigger>
        <TooltipContent side={isMd ? "right" : "top"}>
          <span>
            Search -{" "}
            <span className={"text-neutral-600 text-sm"}>
              {getControlKey()}K
            </span>
          </span>
        </TooltipContent>
      </Tooltip>
    );

    return button;
  };
  const getReservationButton = (type: "desktop" | "mobile" = "desktop") => {
    if (type === "desktop") {
      return null;
    }
    const button = (
      <>
        <button
          className={
            type === "mobile" ? classes.MdPlusButton : classes.PlusButton
          }
          onClick={
            type === "mobile"
              ? () => {
                  mobileReserveSheetDisclosure.open();
                }
              : undefined
          }
        >
          <Plus className={classes.PlusIcon} />
        </button>
      </>
    );

    if (type === "mobile") {
      return button;
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger>{button}</DropdownMenuTrigger>
        <DropdownMenuContent
          align={isMd ? "end" : "center"}
          side={isMd ? "right" : "top"}
        >
          <DropdownMenuItem onClick={handleReserveRoom}>
            {t("navActions.reserveRoom")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleReserveDesk}>
            {t("navActions.reserveDesk")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <nav
        aria-label={"Main Navigation"}
        className={cn(classes.Wrapper, isTeamsApp ? "px-5" : "px-4")}
        style={
          isMd
            ? { width: sidebarSize }
            : {
                background:
                  "linear-gradient(to bottom, #F7F9FB 50%,  rgba(247, 249, 251, 0) 100%)"
              }
        }
      >
        <Link
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (inMoreOrganizations) {
              organizationSwitcherDisclosure.open();
              return;
            }
            routerPush(MARIN_ROUTES.dashboard);
          }}
          href={MARIN_ROUTES.dashboard}
          className={classes.Logo}
        >
          <span>
            <OrgLogo
              logoUrl={
                authenticatedUser?.organization?.squared_logo_url ?? null
              }
              alt={authenticatedUser?.organization?.name ?? "Organization logo"}
              className="size-9 cursor-pointer rounded object-contain"
              fallbackClassName={classes.LogoIcon}
            />
          </span>
        </Link>
        <div
          className={cn(
            classes.SectionsContainer,
            // The list clips what leaves it; a "New" badge hangs off its
            // icon into the nav padding, so the list spans that padding.
            isTeamsApp ? "md:-mx-5 md:px-5" : "md:-mx-4 md:px-4",
            isMobileApp ? "h-28 pb-6" : undefined
          )}
          style={
            isMd
              ? undefined
              : {
                  background:
                    "linear-gradient(to top, #F7F9FB 50%,  rgba(247, 249, 251, 0) 100%)"
                }
          }
        >
          {visibleNavSections.map((section, index) => {
            const menuContent = (
              <div
                className={cn(
                  classes.MenuItem,
                  section.isNew && "overflow-visible",
                  section.active
                    ? classes.MenuItemActive
                    : classes.MenuItemNotActive
                )}
                key={section.id}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="relative inline-flex">
                      {React.createElement(section.icon, {
                        className: classes.Icon,
                        isActive: section.active
                      })}
                      {section.isNew ? (
                        <span className="-top-2 -right-3 pointer-events-none absolute rounded-full bg-primary px-1 font-semibold text-[9px] text-primary-foreground uppercase leading-[14px]">
                          {t("navBadgeNew")}
                        </span>
                      ) : null}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side={isMd ? "right" : "top"} align="center">
                    {t(`navItems.${section.id}`)}
                    {section.isNew ? ` · ${t("navBadgeNew")}` : ""}
                  </TooltipContent>
                </Tooltip>
              </div>
            );
            return (
              <React.Fragment key={section.id}>
                {index === Math.floor(visibleNavSections.length / 2) && (
                  <>
                    {getReservationButton("mobile")}
                    <MobileGlobalReserveActionSheet
                      isOpen={mobileReserveSheetDisclosure.isOpen}
                      onOpenChange={mobileReserveSheetDisclosure.onOpenChange}
                    />
                  </>
                )}

                {section.isMoreButton ? (
                  <LoggedUserNav
                    user={{
                      name: authenticatedUser.user?.name || "",
                      email: authenticatedUser.user?.email || "",
                      avatar: authenticatedUser.user?.profile_photo_url || ""
                    }}
                    trigger={menuContent}
                    onOpenNewFeatures={openAllFeaturesModal}
                    onOpenCancelReservations={cancelReservationsDisclosure.open}
                  />
                ) : (
                  <Link
                    href={
                      section.url +
                      (section.appendFloorId && selectedFloor
                        ? `?floorId=${selectedFloor.id}`
                        : "")
                    }
                    onAfterClick={askTessa?.tessaDisclosure?.close}
                  >
                    {menuContent}
                  </Link>
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className={classes.FooterContainer}>
          {getGlobalSearchButton("desktop")}
          {tessaFeatureFlag && (
            <ChatBotBubble className={roundButtonClassNames} />
          )}
          <ImpersonationButton />
          {getReservationButton("desktop")}
          <LoggedUserNav
            user={{
              name: authenticatedUser.user?.name || "",
              email: authenticatedUser.user?.email || "",
              avatar: authenticatedUser.user?.profile_photo_url || ""
            }}
            onOpenNewFeatures={openAllFeaturesModal}
            onOpenCancelReservations={cancelReservationsDisclosure.open}
          />
        </div>
      </nav>
      <CancelReservationsModal
        isOpen={cancelReservationsDisclosure.isOpen}
        onClose={cancelReservationsDisclosure.close}
      />
      {inMoreOrganizations && (
        <OrganizationSwitcher
          isOpen={organizationSwitcherDisclosure.isOpen}
          onOpenChange={organizationSwitcherDisclosure.onOpenChange}
        />
      )}
    </TooltipProvider>
  );
};
